import { singleton, inject } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { CommonModule } from "../common.module";
import { ApiModule } from "../common/api/api.module";
import { AdvancedExportIntegrationAvailabilityDTO, AdvancedExportIntegrationAvailabilityRequestDTO } from "../common/api/autogenerated";
import { DashboardTooltipButtonManagerModule, UrlData } from "../common/dashboard-tooltip-button-manager/dashboard-tooltip-button-manager.module";
import { ScreenshotModule } from "../common/screenshot.module";
import { UiModule } from "../common/ui.module";
import AdvancedExportPopup from "./AdvancedExportPopup.vue";

@singleton()
export class AdvancedExportModule extends Module {
    public name: string = 'AdvancedExportModule';
    public acceptedLicenseFlags: string[] = ['AdvancedExport'];
    public shouldRun(context: ExtensionContext): boolean { return context === ExtensionContext.vizql; }

    constructor(
        @inject(CommonModule) private common: CommonModule,
        @inject(ApiModule) private apiModule: ApiModule,
        @inject(DashboardTooltipButtonManagerModule) private dashboardTooltipButtonManager: DashboardTooltipButtonManagerModule,
        @inject(ScreenshotModule) private screenshot: ScreenshotModule,
        @inject(UiModule) private uiModule: UiModule) {
        super([common, apiModule, dashboardTooltipButtonManager, screenshot, uiModule]);
    }

    public async run(): Promise<void> {
        this.dashboardTooltipButtonManager.registerButtonFn(async (urlData: UrlData) => {
            const api = await this.apiModule.apiRequest(e => e.apiAdvancedExportIntegrationAvailability(new AdvancedExportIntegrationAvailabilityRequestDTO({
                serverId: window.stm.config.serverId,
                workbookUrl: urlData.workbook,
                viewUrl: urlData.view
            })));
            if (!api.success) {
                this.log.e('Failed to get Advanced Export button availability', api.error);
                return undefined;
            }
            if (!api.value.isAvailable) {
                return undefined;
            }
            return {
                disabled: false,
                text: 'Advanced Export',
                svgXml: `<svg fit="" height="100%" width="100%" preserveAspectRatio="xMidYMid meet" focusable="false" id="ae-btn-icon" style="display: inline-block; width: 22px; height: 22px; transform: scale(0.8); vertical-align: middle; margin-top: -2px;"><g style=""> <path fill="currentColor" d="M23,12L19,8V11H10V13H19V16M1,18V6C1,4.89 1.9,4 3,4H15A2,2 0 0,1 17,6V9H15V6H3V18H15V15H17V18A2,2 0 0,1 15,20H3A2,2 0 0,1 1,18Z"></path></g></svg>`,
                onClicked: () => this.openPopup(api.value)
            };

        });
    }
    private async openPopup(data: AdvancedExportIntegrationAvailabilityDTO) {
        const doc = (await this.common.getDashboardDocument())!;

        // take screenshot first
        const ss = await this.screenshot.takeScreenshot(doc!.body, 5, 500).catch(e => {
            this.log.e('Failed to take a screenshot', e);
            return undefined;
        });
        // create a div
        this.uiModule.injectUI(AdvancedExportPopup, undefined, {
            data,
            screenshot: ss
        });
    }

    public async getSingleCategoricalFilterNames(): Promise<CategoricalFilterInfo[]> {
        try {
            if (this.common.isStandaloneVizQL() || !window.parent.tableau) {
                return [];
            }
            let filters = await window.parent.tableau.VizManager.getVizs()[0].getWorkbook().getActiveSheet().getFiltersAsync();
            // filter out non categorical
            filters = filters.filter(e => e.getFilterType() === 'categorical' && !e.getIsExcludeMode());
            // get applied values and return;
            const ret = await Promise.all(filters.map(async f => {
                const appliedValues = (await f.getAppliedValues()).map(e => e.value);
                return {
                    name: f.getFieldName(),
                    singleAppliedValue: appliedValues.length === 1 ? appliedValues[0]: undefined
                } as CategoricalFilterInfo;
            }));
            return ret;
            

        } catch (e) {
            this.log.w('Failed to get filters for current dashboard', e);
            return [];
        }
    }
}

export interface CategoricalFilterInfo {
    name: string;
    singleAppliedValue: string | undefined;
}